/**
 * Setup Modal (Pflichtangaben-Einrichtung)
 *
 * Packt den Einrichtungs-Prozess (Account-Daten, Benutzer, Lizenz, LinkedIn,
 * Arbeitszeiten, Limits, Signatur) in ein Modal. Es öffnet sich automatisch,
 * solange Pflichtangaben fehlen — und bei jedem neuen Seitenbesuch wieder,
 * bis alles vollständig ist. Der Chat kann es zusätzlich öffnen (Step-Boxen,
 * Empfehlungen), ist aber nicht mehr der primäre Weg.
 *
 * Automatisch öffnet es sich nur für neu registrierte User (`setupWizard` am
 * User, gesetzt bei der Registrierung). Bestandskunden sehen es nicht mehr.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, IconButton, Typography, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import ChatWorkspace from './chat-workspace';
import useAIChatStore from '../../../stores/ai-chat-store';
import { dc } from '../design-tokens';
import { getUserInfo } from '../../../API/user';

const SetupModal: React.FC = () => {
  const { classes } = useStyles();
  const { i18n } = useTranslation();
  const isDe = i18n.language === 'de';

  const { setupModalOpen, setSetupModalOpen, setContext } = useAIChatStore((state) => ({
    setupModalOpen: state.setupModalOpen,
    setSetupModalOpen: state.setSetupModalOpen,
    setContext: state.setContext,
  }));

  const closeModal = () => {
    setSetupModalOpen(false);
    // Kein Tab mehr offen → Chat-Kontext leeren
    setContext({ activeSetupStep: undefined });
  };

  const [allDone, setAllDone] = useState<boolean | null>(null);
  // Auto-Open nur einmal pro Seitenbesuch — manuell geschlossen bleibt zu,
  // kommt aber beim nächsten Laden wieder, solange Angaben fehlen.
  const autoOpenedRef = useRef(false);
  // Nur neu registrierte User bekommen den Wizard automatisch (null = lädt noch)
  const [wizardEnabled, setWizardEnabled] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    getUserInfo().then((user) => {
      if (!cancelled) setWizardEnabled(user?.setupWizard === true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleStatusChange = useCallback(
    (done: boolean) => {
      setAllDone(done);
    },
    []
  );

  useEffect(() => {
    if (wizardEnabled === true && allDone === false && !autoOpenedRef.current) {
      autoOpenedRef.current = true;
      setSetupModalOpen(true);
    }
    // Alles vollständig → Modal darf zu
    if (allDone === true && setupModalOpen && autoOpenedRef.current) {
      // nicht sofort zuschlagen — der User sieht die "Fertig"-Ansicht;
      // schließen bleibt ihm überlassen.
    }
  }, [wizardEnabled, allDone, setSetupModalOpen, setupModalOpen]);

  return (
    <>
      {/* Status-Probe: lädt den Vollständigkeits-Status auch bei geschlossenem
          Modal, damit das Auto-Open entscheiden kann — unsichtbar. */}
      {wizardEnabled === true && allDone === null && !setupModalOpen && (
        <Box className={classes.hiddenProbe}>
          <ChatWorkspace variant="modal" onStatusChange={handleStatusChange} syncContext={false} />
        </Box>
      )}

      <Dialog
        open={setupModalOpen}
        onClose={() => {
          // Neukunden: Solange Pflichtangaben fehlen, gibt es KEIN Schließen —
          // weder Backdrop-Klick noch Escape. Bestandskunden (manuell geöffnet) dürfen immer zu.
          if (wizardEnabled && allDone !== true) return;
          closeModal();
        }}
        fullScreen
        classes={{ paper: classes.paper }}
      >
        <Box className={classes.titleRow}>
          <Typography variant="subtitle1" className={classes.title}>
            {isDe ? 'Konto einrichten' : 'Set up your account'}
          </Typography>
          <Typography variant="caption" className={classes.subtitle}>
            {isDe
              ? 'Pflichtangaben — nötig, bevor Kampagnen laufen können.'
              : 'Required details — needed before campaigns can run.'}
          </Typography>
          {(allDone === true || wizardEnabled === false) && (
            <IconButton
              size="small"
              onClick={closeModal}
              aria-label={isDe ? 'Schließen' : 'Close'}
              className={classes.closeButton}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
        <DialogContent className={classes.content}>
          <ChatWorkspace variant="modal" onStatusChange={handleStatusChange} />
        </DialogContent>
      </Dialog>
    </>
  );
};

const useStyles = makeStyles()(() => ({
  hiddenProbe: {
    position: 'fixed',
    width: 0,
    height: 0,
    overflow: 'hidden',
    visibility: 'hidden',
    pointerEvents: 'none',
  },
  paper: {
    backgroundColor: dc.bg,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 12,
    padding: '18px 32px 12px',
    backgroundColor: dc.surface,
    borderBottom: `1px solid ${dc.border}`,
  },
  title: {
    fontWeight: 700,
    color: dc.ink,
  },
  subtitle: {
    flex: 1,
    color: dc.muted,
  },
  closeButton: {
    alignSelf: 'center',
  },
  content: {
    padding: '0 !important',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
}));

export default SetupModal;
